(function (root, factory) {
  const runtimeKey = '__ODCCA_BOOKING_DATE_RUNTIME__';
  const api = root && root[runtimeKey]?.api ? root[runtimeKey].api : factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.ODCCABookingDate = api;
})(typeof window !== 'undefined' ? window : this, function (root) {
  'use strict';

  // Shopify can evaluate a Custom Liquid block more than once while the Theme
  // Editor or a section refresh is active.  The outer wrapper reuses this API;
  // this marker lets the runtime below share its single boot/lifecycle wiring.
  const runtimeKey = '__ODCCA_BOOKING_DATE_RUNTIME__';

  const BOOKING_TIME_ZONE = 'Africa/Casablanca';
  const MINIMUM_NOTICE_HOURS = 24;

  const SESSION_BY_VARIANT_ID = Object.freeze({
    '50823012057377': Object.freeze({ key: 'Morning', start: '09:30', end: '12:30', durationMinutes: 180, classType: 'Standard' }),
    '50823012090145': Object.freeze({ key: 'Afternoon', start: '16:00', end: '19:00', durationMinutes: 180, classType: 'Standard' }),
    '51874697183521': Object.freeze({ key: 'Express', start: '14:00', end: '15:00', durationMinutes: 60, classType: 'Express' }),
    // Split-product variants (the original combined-product IDs remain for
    // existing links and older orders).
    '53418040557857': Object.freeze({ key: 'Morning', start: '09:30', end: '12:30', durationMinutes: 180, classType: 'Standard' }),
    '53418040590625': Object.freeze({ key: 'Afternoon', start: '16:00', end: '19:00', durationMinutes: 180, classType: 'Standard' }),
    '53476263428385': Object.freeze({ key: 'Morning', start: '09:30', end: '12:30', durationMinutes: 180, classType: 'Standard' }),
    '53476263461153': Object.freeze({ key: 'Afternoon', start: '16:00', end: '19:00', durationMinutes: 180, classType: 'Standard' }),
    '53476554440993': Object.freeze({ key: 'Express', start: '14:00', end: '15:00', durationMinutes: 60, classType: 'Express' }),
    // Current split-product Express variant; keep the prior ID for historical orders.
    '53418810114337': Object.freeze({ key: 'Express', start: '14:00', end: '15:00', durationMinutes: 60, classType: 'Express' }),
    '51642236305697': Object.freeze({ key: 'Express', start: '14:00', end: '15:00', durationMinutes: 60, classType: 'Express' }),
  });
  const VARIANT_ID_BY_OPTION_VALUE = Object.freeze({
    'Morning Session 9:30am-12:30pm': '50823012057377',
    'Afternoon Session 4:00-7:00pm': '50823012090145',
    '1-hour Express Session 2:00-3:00pm': '51874697183521',
    'Session du matin 9:30-12:30': '50823012057377',
    "Session de l'après-midi 16:00-19:00": '50823012090145',
    "Session Express d'une heure 14:00-15:00": '51874697183521',
    'Séance du matin 9h30–12h30': '50823012057377',
    'Séance de l’après-midi 16h00–19h00': '50823012090145',
    'Séance express 14h00–15h00': '51874697183521',
  });
  const SPLIT_VARIANT_ID_BY_OPTION_VALUE = Object.freeze({
    'Morning Session 9:30am-12:30pm': '53418040557857',
    'Afternoon Session 4:00-7:00pm': '53418040590625',
    'Express Session 2:00-3:00pm': '53418810114337',
    // Historical option/variant pairing retained for existing orders and links.
    '1-hour Express Session 2:00-3:00pm': '51642236305697',
    'Session du matin 9:30-12:30': '53418040557857',
    "Session de l'après-midi 16:00-19:00": '53418040590625',
    "Session Express d'une heure 14:00-15:00": '51642236305697',
    'Séance du matin 9h30–12h30': '53418040557857',
    'Séance de l’après-midi 16h00–19h00': '53418040590625',
    'Séance express 14h00–15h00': '53418810114337',
  });
  const MARRAKESH_VARIANT_ID_BY_OPTION_VALUE = Object.freeze({
    'Morning Session 9:30am-12:30pm': '53476263428385',
    'Afternoon Session 4:00-7:00pm': '53476263461153',
    'Express Session 2:00-3:00pm': '53476554440993',
    'Session du matin 9:30-12:30': '53476263428385',
    "Session de l'après-midi 16:00-19:00": '53476263461153',
    "Session Express d'une heure 14:00-15:00": '53476554440993',
    'Séance du matin 9h30–12h30': '53476263428385',
    'Séance de l’après-midi 16h00–19h00': '53476263461153',
    'Séance express 14h00–15h00': '53476554440993',
  });
  const SPLIT_VARIANT_IDS = new Set(Object.values(SPLIT_VARIANT_ID_BY_OPTION_VALUE));

  function partsInTimeZone(instant, timeZone) {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(instant);
    return Object.fromEntries(parts.filter((part) => part.type !== 'literal').map((part) => [part.type, Number(part.value)]));
  }

  function timeZoneOffsetMs(instant, timeZone) {
    const parts = partsInTimeZone(instant, timeZone);
    const representedAsUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
    return representedAsUtc - instant.getTime();
  }

  function zonedDateTimeToInstant(dateIso, time24, timeZone = BOOKING_TIME_ZONE) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateIso || ''))) return null;
    if (!/^\d{2}:\d{2}$/.test(String(time24 || ''))) return null;
    const [year, month, day] = dateIso.split('-').map(Number);
    const [hour, minute] = time24.split(':').map(Number);
    const utcGuess = Date.UTC(year, month - 1, day, hour, minute, 0);
    let instant = new Date(utcGuess);
    for (let index = 0; index < 3; index += 1) {
      instant = new Date(utcGuess - timeZoneOffsetMs(instant, timeZone));
    }
    const roundTrip = partsInTimeZone(instant, timeZone);
    if (roundTrip.year !== year || roundTrip.month !== month || roundTrip.day !== day || roundTrip.hour !== hour || roundTrip.minute !== minute) return null;
    return instant;
  }

  function validateSelection({ variantId, dateIso, now = new Date(), minimumNoticeHours = MINIMUM_NOTICE_HOURS }) {
    const session = SESSION_BY_VARIANT_ID[String(variantId || '')];
    if (!session) return { valid: false, code: 'unsupported_session', message: 'Please choose a valid class session.' };
    if (!dateIso) return { valid: false, code: 'missing_date', message: 'Please choose your class date.' };
    const classStart = zonedDateTimeToInstant(dateIso, session.start);
    if (!classStart) return { valid: false, code: 'invalid_date', message: 'Please choose a valid class date.' };
    const noticeMs = classStart.getTime() - new Date(now).getTime();
    if (noticeMs < minimumNoticeHours * 60 * 60 * 1000) {
      return {
        valid: false,
        code: 'minimum_notice',
        message: `This session must be booked at least ${minimumNoticeHours} hours in advance. Please choose a later date or session.`,
        classStart,
        session,
      };
    }
    return { valid: true, code: 'valid', message: '', classStart, session };
  }

  const instances = new WeakMap();

  function initBookingSelector(container) {
    if (!container || container.nodeType !== 1) return null;
    if (instances.has(container) || container.dataset.odccaInitialized === 'true') return instances.get(container) || null;

    const nearestProductInfo = container.closest('.product__info-container');
    const form = container.closest('form[data-type="add-to-cart-form"]')
      || nearestProductInfo?.querySelector('form[data-type="add-to-cart-form"]')
      || document.querySelector('form[data-type="add-to-cart-form"]');
    const dateInput = container.querySelector('[data-booking-date]');
    const error = container.querySelector('[data-booking-error]');
    const summary = container.querySelector('[data-booking-summary]');
    if (!form || !dateInput || !error || !summary) return null;
    const errorHome = error.parentNode;
    const errorHomeNextSibling = error.nextSibling;

    const productInfo = nearestProductInfo || form.closest('.product__info-container') || form.parentElement;
    if (!productInfo) return null;
    container.dataset.odccaInitialized = 'true';

    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const cleanupListeners = [];
    const scheduled = new Set();
    const scheduledByKey = new Map();
    let active = true;
    let variantSyncToken = 0;
    let variantInput = null;
    const formId = form.getAttribute('id');
    const hideSession = container.dataset.hideSession === 'true';
    const propertyInputs = Object.fromEntries(
      Array.from(container.querySelectorAll('[data-booking-property]')).map((input) => [input.dataset.bookingProperty, input]),
    );
    const on = (target, type, listener, options = false) => {
      if (!target?.addEventListener) return;
      const listenerOptions = controller
        ? {
          ...(typeof options === 'object' && options !== null ? options : { capture: Boolean(options) }),
          signal: controller.signal,
        }
        : options;
      target.addEventListener(type, listener, listenerOptions);
      if (!controller) cleanupListeners.push(() => target.removeEventListener(type, listener, options));
    };
    const schedule = (callback, delay = 0, key = '') => {
      if (key && scheduledByKey.has(key)) {
        const previous = scheduledByKey.get(key);
        clearTimeout(previous);
        scheduled.delete(previous);
      }
      const timer = setTimeout(() => {
        scheduled.delete(timer);
        if (key && scheduledByKey.get(key) === timer) scheduledByKey.delete(key);
        if (active) callback();
      }, delay);
      scheduled.add(timer);
      if (key) scheduledByKey.set(key, timer);
      return timer;
    };
    const activeForm = () => (formId && document.getElementById(formId)) || form;
    const submitControls = () => Array.from(activeForm().querySelectorAll('[type="submit"]'));
    const bookingFormMarker = 'data-odcca-booking-form';
    const bookingFormMarkers = new Map();
    const pendingFormActionRestores = new Set();
    const pendingRouteRestores = new Set();
    const markBookingForm = () => {
      const currentForm = activeForm();
      if (!currentForm) return null;
      if (!bookingFormMarkers.has(currentForm)) bookingFormMarkers.set(currentForm, currentForm.hasAttribute(bookingFormMarker));
      currentForm.setAttribute(bookingFormMarker, '');
      return currentForm;
    };
    const placeError = () => {
      const currentForm = markBookingForm();
      if (!currentForm) return;
      const buyButtons = currentForm.querySelector('.product-form__buttons') || submitControls()[0]?.parentElement;
      if (buyButtons && error.parentNode !== buyButtons) buyButtons.prepend(error);
    };
    const currentVariantPicker = () => productInfo.querySelector('variant-selects, variant-radios');
    const selectedOption = () => Array.from(productInfo.querySelectorAll('input[type="radio"]'))
      .find((option) => option.checked && (option.getAttribute('form') === formId || option.form === activeForm()));
    const isSplitProduct = (currentVariantId = '') => SPLIT_VARIANT_IDS.has(String(currentVariantId))
      || ['10524842590497', '10114333540641'].includes(container.dataset.bookingProductId);
    const isMarrakeshProduct = () => ['10534128189729', '10534148407585'].includes(container.dataset.bookingProductId);
    const setInputValue = (input, value) => {
      if (!input || input.value === value) return;
      input.value = value;
      if (input.getAttribute('value') !== value) input.setAttribute('value', value);
    };
    const ensureVariantInput = () => {
      const currentForm = activeForm();
      const existingOverride = currentForm.querySelector('[data-odcca-variant-override]');
      if (existingOverride) {
        variantInput = existingOverride;
        return variantInput;
      }
      const source = currentForm.querySelector('[name="id"]');
      if (!source) return null;
      const override = document.createElement('input');
      override.type = 'hidden';
      override.name = 'id';
      override.className = source.className;
      override.dataset.odccaVariantOverride = '';
      currentForm.prepend(override);
      variantInput = override;
      return variantInput;
    };
    const writeVariantId = (id) => {
      if (!id) return;
      const currentForm = activeForm();
      const inputs = Array.from(currentForm.querySelectorAll('[name="id"]'));
      if (!inputs.length) return;
      const override = currentForm.querySelector('[data-odcca-variant-override]');
      inputs.forEach((input) => setInputValue(input, id));
      variantInput = override || inputs[0];
    };
    const normalizeCartRoutes = () => {
      const currentForm = markBookingForm();
      if (!currentForm) return;
      const action = currentForm.getAttribute('action');
      if (action?.startsWith('/fr/cart/')) {
        const normalizedAction = action.replace(/^\/fr/, '');
        currentForm.setAttribute('action', normalizedAction);
        const changedAction = { form: currentForm, original: action, normalized: normalizedAction };
        pendingFormActionRestores.add(changedAction);
        schedule(() => {
          if (currentForm.getAttribute('action') === normalizedAction) currentForm.setAttribute('action', action);
          pendingFormActionRestores.delete(changedAction);
        });
      }
      if (typeof window === 'undefined' || !window.routes) return;
      // Dawn's product-form handler reads only cart_add_url for this request.
      // Keep the workaround limited to that route so cart change/update
      // consumers elsewhere on the page are never mutated as a side effect.
      const key = 'cart_add_url';
      const original = window.routes[key];
      if (typeof original !== 'string' || !original.startsWith('/fr/cart/')) return;
      const normalized = original.replace(/^\/fr/, '');
      window.routes[key] = normalized;
      const changedRoute = { key, original, normalized };
      pendingRouteRestores.add(changedRoute);
      schedule(() => {
        if (window.routes?.[key] === normalized) window.routes[key] = original;
        pendingRouteRestores.delete(changedRoute);
      });
    };
    const normalizeBookingSelectors = () => {
      Array.from(productInfo.querySelectorAll('[data-odcca-booking-selector]'))
        .forEach((item, index) => { item.hidden = index > 0; });
    };
    const placeSelector = () => {
      const titleBlock = productInfo.querySelector('.product__title');
      const priceBlock = productInfo.querySelector('[id^="price-"]');
      const placementAnchor = priceBlock || titleBlock;
      if (placementAnchor && placementAnchor.nextElementSibling !== container) placementAnchor.after(container);
    };
    const decorateSessionPicker = () => {
      const variantPicker = currentVariantPicker();
      if (!variantPicker) return;
      if (hideSession) {
        if (!variantPicker.hidden) variantPicker.hidden = true;
        if (variantPicker.style.getPropertyValue('display') !== 'none') variantPicker.style.setProperty('display', 'none', 'important');
        return;
      }
      if (variantPicker.hidden) variantPicker.hidden = false;
      if (variantPicker.style.getPropertyValue('display')) variantPicker.style.removeProperty('display');
      const sessionLegend = variantPicker.querySelector('fieldset legend');
      if (!sessionLegend) return;
      const sessionHeading = container.dataset.stepSession || 'Step 2: Choose your session';
      if (sessionLegend.textContent !== sessionHeading) sessionLegend.textContent = sessionHeading;
      sessionLegend.classList.add('odcca-session-heading');
      if (!variantPicker.querySelector('[data-odcca-session-help]')) {
        const sessionHelp = document.createElement('p');
        sessionHelp.className = 'caption odcca-session-help';
        sessionHelp.dataset.odccaSessionHelp = '';
        sessionHelp.textContent = container.dataset.sessionHelp || 'Times are shown in Rabat local time.';
        sessionLegend.after(sessionHelp);
      }
    };
    const localizedMessages = {
      unsupported_session: container.dataset.errorSession,
      missing_date: container.dataset.errorMissingDate,
      invalid_date: container.dataset.errorInvalidDate,
      minimum_notice: container.dataset.errorMinimumNotice,
    };
    const localizedSessions = {
      Morning: container.dataset.sessionMorning || 'Morning',
      Afternoon: container.dataset.sessionAfternoon || 'Afternoon',
      Express: container.dataset.sessionExpress || 'Express',
    };
    const minimumDate = partsInTimeZone(new Date(Date.now() + MINIMUM_NOTICE_HOURS * 60 * 60 * 1000), BOOKING_TIME_ZONE);
    dateInput.min = `${minimumDate.year}-${String(minimumDate.month).padStart(2, '0')}-${String(minimumDate.day).padStart(2, '0')}`;
    Object.values(propertyInputs).forEach((input) => { if (formId) input.setAttribute('form', formId); });
    normalizeBookingSelectors();
    placeSelector();
    decorateSessionPicker();
    placeError();

    function selectedVariantId() {
      ensureVariantInput();
      const currentForm = activeForm();
      const currentVariantId = String(variantInput?.value || currentForm.querySelector('[name="id"]')?.value || '');
      if (hideSession && container.dataset.bookingProductId === '10114333540641') return '53418810114337';
      const option = selectedOption();
      const mapped = isMarrakeshProduct()
        ? MARRAKESH_VARIANT_ID_BY_OPTION_VALUE[option?.value]
        : isSplitProduct(currentVariantId)
        ? SPLIT_VARIANT_ID_BY_OPTION_VALUE[option?.value]
        : VARIANT_ID_BY_OPTION_VALUE[option?.value];
      return String(mapped || currentVariantId || new URLSearchParams(location.search).get('variant') || '');
    }

    const syncSelectedVariant = (option) => {
      const currentVariantId = String(variantInput?.value || '');
      const chosenVariantId = (isMarrakeshProduct()
        ? MARRAKESH_VARIANT_ID_BY_OPTION_VALUE[option?.value]
        : isSplitProduct(currentVariantId)
        ? SPLIT_VARIANT_ID_BY_OPTION_VALUE[option?.value]
        : VARIANT_ID_BY_OPTION_VALUE[option?.value]);
      if (!chosenVariantId) return;
      const token = ++variantSyncToken;
      option.checked = true;
      writeVariantId(chosenVariantId);
      render();
      schedule(() => { if (token === variantSyncToken) { decorateSessionPicker(); render(); } }, 0, 'variant-immediate');
      schedule(() => { if (token === variantSyncToken) { decorateSessionPicker(); render(); } }, 250, 'variant-deferred');
    };

    function render() {
      placeError();
      const selectedId = selectedVariantId();
      const option = selectedOption();
      const currentVariantId = String(variantInput?.value || '');
      const selectedOptionId = (isMarrakeshProduct()
        ? MARRAKESH_VARIANT_ID_BY_OPTION_VALUE[option?.value]
        : isSplitProduct(currentVariantId)
        ? SPLIT_VARIANT_ID_BY_OPTION_VALUE[option?.value]
        : VARIANT_ID_BY_OPTION_VALUE[option?.value]);
      const desiredVariantId = selectedOptionId || (hideSession ? selectedId : '');
      if (desiredVariantId && desiredVariantId !== currentVariantId) writeVariantId(desiredVariantId);
      const result = validateSelection({ variantId: selectedId, dateIso: dateInput.value });
      const message = result.valid ? '' : (localizedMessages[result.code] || result.message);
      if (error.textContent !== message) error.textContent = message;
      if (error.hidden === Boolean(!message)) error.hidden = !message;
      dateInput.setCustomValidity(message);
      submitControls().forEach((control) => {
        const disabled = !result.valid;
        if (control.disabled !== disabled) control.disabled = disabled;
        if (control.getAttribute('aria-disabled') !== String(disabled)) control.setAttribute('aria-disabled', String(disabled));
      });
      if (!result.valid) {
        summary.hidden = true;
        Object.entries(propertyInputs).forEach(([key, input]) => {
          if (key !== 'date' && input.value) setInputValue(input, '');
        });
        return result;
      }
      const values = {
        date: dateInput.value,
        session: result.session.key,
        start: result.session.start,
        end: result.session.end,
        duration: String(result.session.durationMinutes),
        timezone: BOOKING_TIME_ZONE,
        classType: result.session.classType,
      };
      Object.entries(values).forEach(([key, value]) => {
        if (propertyInputs[key]) setInputValue(propertyInputs[key], value);
      });
      const nextSummary = `${dateInput.value} · ${localizedSessions[result.session.key]} · ${result.session.start}–${result.session.end} (${BOOKING_TIME_ZONE})`;
      if (summary.textContent !== nextSummary) summary.textContent = nextSummary;
      summary.hidden = false;
      return result;
    }

    const handleChange = (event) => {
      if (event.target?.matches?.('input[type="radio"]') && (event.target.getAttribute('form') === formId || event.target.form === activeForm())) {
        syncSelectedVariant(event.target);
      } else if (event.target?.form === activeForm() || event.target?.matches?.('[name="id"], [name^="options"]')) {
        schedule(() => { decorateSessionPicker(); render(); }, 0, 'change-render');
      }
    };
    const handleClick = (event) => {
      const option = event.target?.closest?.('input[type="radio"], label');
      if (option?.matches?.('input[type="radio"]')) syncSelectedVariant(option);
      else if (option?.matches?.('label[for]')) {
        const radio = Array.from(productInfo.querySelectorAll('input[type="radio"]'))
          .find((candidate) => candidate.id === option.htmlFor);
        if (radio?.matches?.('input[type="radio"]')) schedule(() => syncSelectedVariant(radio), 0);
      }
    };
    const handleSubmit = (event) => {
      normalizeCartRoutes();
      const result = render();
      const candidate = selectedVariantId();
      if (candidate) writeVariantId(candidate);
      if (!result.valid) {
        event.preventDefault();
        dateInput.reportValidity();
        dateInput.focus();
      }
    };
    const handleFormData = (event) => {
      normalizeCartRoutes();
      const submittedForm = event.target;
      if (!submittedForm?.matches?.('form[data-type="add-to-cart-form"]')) return;
      const candidate = selectedVariantId() || new URLSearchParams(location.search).get('variant');
      if (candidate) event.formData.set('id', candidate);
    };
    on(dateInput, 'input', render);
    on(dateInput, 'change', render);
    on(productInfo, 'click', handleClick, true);
    on(productInfo, 'change', handleChange, true);
    on(productInfo, 'submit', handleSubmit, true);
    on(form, 'formdata', handleFormData, true);
    on(productInfo, 'formdata', handleFormData, true);
    render();

    const instance = {
      cleanup() {
        if (!active) return;
        active = false;
        scheduled.forEach((timer) => clearTimeout(timer));
        scheduled.clear();
        scheduledByKey.clear();
        if (controller) controller.abort();
        cleanupListeners.splice(0).forEach((cleanup) => cleanup());
        pendingFormActionRestores.forEach(({ form, original, normalized }) => {
          if (form?.getAttribute('action') === normalized) form.setAttribute('action', original);
        });
        pendingFormActionRestores.clear();
        pendingRouteRestores.forEach(({ key, original, normalized }) => {
          if (window.routes?.[key] === normalized) window.routes[key] = original;
        });
        pendingRouteRestores.clear();
        bookingFormMarkers.forEach((hadMarker, markedForm) => {
          if (!hadMarker) markedForm.removeAttribute(bookingFormMarker);
        });
        if (errorHome?.isConnected && error.parentNode !== errorHome) {
          if (errorHomeNextSibling?.parentNode === errorHome) errorHome.insertBefore(error, errorHomeNextSibling);
          else errorHome.append(error);
        }
        container.removeAttribute('data-odcca-initialized');
        instances.delete(container);
      },
    };
    instances.set(container, instance);
    return instance;
  }

  function boot(scope = document) {
    if (!scope) return;
    const selectors = [];
    if (scope.matches?.('[data-odcca-booking-selector]')) selectors.push(scope);
    selectors.push(...Array.from(scope.querySelectorAll?.('[data-odcca-booking-selector]') || []));
    selectors.forEach(initBookingSelector);
  }

  function cleanupIn(scope = document) {
    if (!scope) return;
    const selectors = [];
    if (scope.matches?.('[data-odcca-booking-selector]')) selectors.push(scope);
    selectors.push(...Array.from(scope.querySelectorAll?.('[data-odcca-booking-selector]') || []));
    selectors.forEach((selector) => instances.get(selector)?.cleanup());
  }

  const api = {
    BOOKING_TIME_ZONE,
    MINIMUM_NOTICE_HOURS,
    SESSION_BY_VARIANT_ID,
    VARIANT_ID_BY_OPTION_VALUE,
    SPLIT_VARIANT_ID_BY_OPTION_VALUE,
    zonedDateTimeToInstant,
    validateSelection,
    initBookingSelector,
  };

  const runtime = { api, boot, cleanupIn };
  if (root && typeof root === 'object') root[runtimeKey] = runtime;
  if (typeof document !== 'undefined') {
    const start = () => boot(document);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
    document.addEventListener('shopify:section:load', (event) => boot(event.target || document));
    document.addEventListener('shopify:section:unload', (event) => cleanupIn(event.target || document));
    window.addEventListener('pageshow', () => boot(document));
  }
  return api;
});
